import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  Inject,
  AfterViewInit,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { IremboSelfieVerificationComponent } from '../../irembo-selfie-verification/irembo-selfie-verification.component';
import { FormStateService } from '../../../services/formly/form-state.service';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { IEnvironment } from '../../../models/environment.model';
import { v4 as uuidv4 } from 'uuid';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

interface PhoneNumber {
  key: string;
  value: string;
}

const INTEGRATION_URL = '/integration/v1/fetch/sync';

@Component({
  standalone: false,
  selector: 'irembogov-custom-selfie-verification',
  templateUrl: './custom-selfie-verification.component.html',
  styleUrls: ['./custom-selfie-verification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomSelfieVerificationComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit, OnDestroy, AfterViewInit
{
  private subscription: Subscription = new Subscription();
  private phoneNumbers: PhoneNumber[] = [];
  private preEnrollmentTempId: string | null = null;
  private legalGuardianId: string | null = null;

  // Track values at verification time for change detection
  private verifiedPhoneNumbers!: string;
  private verifiedPreEnrollmentId: string | null = null;
  private verifiedLegalGuardianId: string | null = null;

  statusClass: string | undefined;
  verificationCompleted = false;

  constructor(
    private readonly modalService: NgbModal,
    private readonly formStateService: FormStateService,
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly translateService: TranslateService,
    private readonly cd: ChangeDetectorRef,
    @Inject('environment') private readonly environment: IEnvironment
  ) {
    super();
  }

  ngOnInit(): void {
    // Kick off model loading now — before user clicks anything.
    // Uses the modal's own static cache so when it opens, modelsLoaded is
    // already true and detection starts the instant the camera is ready.
    IremboSelfieVerificationComponent.preload();

    this.setupPhoneNumbersSubscription();
    this.setupPreEnrollmentIdSubscription();
    this.setupLegalGuardianIdSubscription();
    this.checkInitialVerificationStatus();
  }

  ngAfterViewInit(): void {
    // Mark verification as completed if a value already exists
    setTimeout(() => {
      this.checkInitialVerificationStatus();
    }, 0);
  }

  private checkInitialVerificationStatus(): void {
    // If the field already has a value, assume it's successfully verified
    if (this.field?.formControl?.value) {
      this.verificationCompleted = true;

      // Store current phone numbers and IDs as verified values
      // to prevent unnecessary resets
      this.verifiedPhoneNumbers = JSON.stringify(this.phoneNumbers);
      this.verifiedPreEnrollmentId = this.preEnrollmentTempId;
      this.verifiedLegalGuardianId = this.legalGuardianId;

      this.cd.detectChanges();
    }
  }

  private setupPhoneNumbersSubscription(): void {
    if (this.field?.props?.['phoneNumbersKey']) {
      this.subscription.add(
        this.field.options?.fieldChanges
          ?.pipe(
            distinctUntilChanged(
              (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
            )
          )
          .subscribe(changes => {
            if (changes.field.key === this.field.props['phoneNumbersKey']) {
              this.updatePhoneNumbers(changes.value);
            }
          })
      );

      this.subscription.add(
        this.formStateService
          .getFormlyFormStateSubject()
          .pipe(
            distinctUntilChanged(
              (prev, curr) =>
                JSON.stringify(prev?.[this.field.props['phoneNumbersKey']]) ===
                JSON.stringify(curr?.[this.field.props['phoneNumbersKey']])
            )
          )
          .subscribe(formState => {
            const phoneNumbersKey = this.field.props['phoneNumbersKey'];
            if (formState && phoneNumbersKey) {
              this.updatePhoneNumbers(formState[phoneNumbersKey]);
            }
          })
      );
      const initialValue = this.form.get(
        this.field.props['phoneNumbersKey']
      )?.value;
      if (initialValue) {
        this.updatePhoneNumbers(initialValue);
      }
    }
  }

  private setupPreEnrollmentIdSubscription(): void {
    if (this.field?.props?.['preEnrollmentTempIdKey']) {
      this.subscription.add(
        this.field.options?.fieldChanges
          ?.pipe(
            distinctUntilChanged(
              (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
            )
          )
          .subscribe(changes => {
            if (
              changes.field.key === this.field.props['preEnrollmentTempIdKey']
            ) {
              this.updatePreEnrollmentTempId(changes.value);
            }
          })
      );

      this.subscription.add(
        this.formStateService
          .getFormlyFormStateSubject()
          .pipe(
            distinctUntilChanged(
              (prev, curr) =>
                JSON.stringify(
                  prev?.[this.field.props['preEnrollmentTempIdKey']]
                ) ===
                JSON.stringify(
                  curr?.[this.field.props['preEnrollmentTempIdKey']]
                )
            )
          )
          .subscribe(formState => {
            const preEnrollmentTempIdKey =
              this.field.props['preEnrollmentTempIdKey'];
            if (formState && preEnrollmentTempIdKey) {
              this.updatePreEnrollmentTempId(formState[preEnrollmentTempIdKey]);
            }
          })
      );

      const initialValue = this.form.get(
        this.field.props['preEnrollmentTempIdKey']
      )?.value;
      if (initialValue) {
        this.updatePreEnrollmentTempId(initialValue);
      }
    }
  }

  private setupLegalGuardianIdSubscription(): void {
    if (this.field?.props?.['legalGuardianIdKey']) {
      this.subscription.add(
        this.field.options?.fieldChanges
          ?.pipe(
            distinctUntilChanged(
              (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
            )
          )
          .subscribe(changes => {
            if (changes.field.key === this.field.props['legalGuardianIdKey']) {
              this.updateLegalGuardianId(changes.value);
            }
          })
      );

      this.subscription.add(
        this.formStateService
          .getFormlyFormStateSubject()
          .pipe(
            distinctUntilChanged(
              (prev, curr) =>
                JSON.stringify(
                  prev?.[this.field.props['legalGuardianIdKey']]
                ) ===
                JSON.stringify(curr?.[this.field.props['legalGuardianIdKey']])
            )
          )
          .subscribe(formState => {
            const legalGuardianIdKey = this.field.props['legalGuardianIdKey'];
            if (formState && legalGuardianIdKey) {
              this.updateLegalGuardianId(formState[legalGuardianIdKey]);
            }
          })
      );

      const initialValue = this.form.get(
        this.field.props['legalGuardianIdKey']
      )?.value;
      if (initialValue) {
        this.updateLegalGuardianId(initialValue);
      }
    }
  }

  private updatePhoneNumbers(value: any): void {
    try {
      let parsedPhoneNumbers: PhoneNumber[];

      if (typeof value === 'string') {
        parsedPhoneNumbers = JSON.parse(value);
      } else if (Array.isArray(value)) {
        parsedPhoneNumbers = value;
      } else {
        parsedPhoneNumbers = [];
      }

      // Create a stringified version for comparison
      const phoneNumbersString = JSON.stringify(parsedPhoneNumbers);

      // Store the phone numbers
      this.phoneNumbers = parsedPhoneNumbers;

      // If verification was completed and the phone numbers have changed, reset
      if (
        this.verificationCompleted &&
        this.verifiedPhoneNumbers !== phoneNumbersString
      ) {
        this.resetVerification();
      }
    } catch (e) {
      this.phoneNumbers = [];

      // If verification was completed and we now have no phone numbers, reset
      if (this.verificationCompleted && this.verifiedPhoneNumbers) {
        this.resetVerification();
      }
    }
  }

  private updatePreEnrollmentTempId(value: any): void {
    const newPreEnrollmentId =
      value && typeof value === 'string' ? value : null;

    // Store the pre-enrollment ID
    this.preEnrollmentTempId = newPreEnrollmentId;

    // If verification was completed and the ID has changed, reset
    if (
      this.verificationCompleted &&
      this.verifiedPreEnrollmentId !== newPreEnrollmentId
    ) {
      this.resetVerification();
    }
  }

  private updateLegalGuardianId(value: any): void {
    const newLegalGuardianId =
      value && typeof value === 'string' ? value : null;

    // Store the legal guardian ID
    this.legalGuardianId = newLegalGuardianId;

    // If verification was completed and the ID has changed, reset
    if (
      this.verificationCompleted &&
      this.verifiedLegalGuardianId !== newLegalGuardianId
    ) {
      this.resetVerification();
    }
  }

  private resetVerification(): void {
    console.log('Verification data changed - resetting verification status');
    this.verificationCompleted = false;
    this.field.formControl?.setValue(null);
    this.cd.detectChanges();
  }

  private updateUrlWithApiGatewayBaseUrl(url: string): string {
    const portalApiGatewayBaseUrl: string | undefined =
      this.environment.apiGatewayBaseUrl;
    if (!portalApiGatewayBaseUrl) {
      throw new Error(
        'useBaseUrl: portal environment ApiGatewayBaseUrl property is required'
      );
    } else {
      url = `${portalApiGatewayBaseUrl}${url}`;
    }

    return url;
  }

  submitVerification(data: {
    selfie: string;
    phoneNumberKey?: string;
    phoneNumber?: string;
  }) {
    const { endpointCode } = this.field?.props ?? {};

    if (!endpointCode) {
      return;
    }

    // Check which ID to use - prioritize preEnrollmentTempId if both are available
    const idToUse = this.preEnrollmentTempId ?? this.legalGuardianId;
    if (!idToUse) {
      return;
    }

    const request_url = this.updateUrlWithApiGatewayBaseUrl(INTEGRATION_URL);

    const payload: any = {
      selfie: data.selfie,
    };

    // Add the appropriate ID to payload
    if (this.preEnrollmentTempId) {
      payload.preEnrollmentTempId = this.preEnrollmentTempId;
    } else if (this.legalGuardianId) {
      payload.legalGuardianId = this.legalGuardianId;
    }

    if (data.phoneNumberKey) {
      payload.phoneNumberKey = data.phoneNumberKey;
    } else if (data.phoneNumber) {
      payload.phoneNumber = data.phoneNumber;
    }

    const params = {
      endpointCode,
      callerId: uuidv4(),
      payload,
      requester: uuidv4(),
    };

    this.statusClass = 'fetching';

    return this.http.post<Record<string, unknown>>(request_url, params);
  }

  // New method for face detection endpoint
  submitFaceDetection(data: { selfie: string }) {
    const { detectFaceEndpoint } = this.field?.props ?? {};

    if (!detectFaceEndpoint) {
      return;
    }

    // Check which ID to use - prioritize preEnrollmentTempId if both are available
    const idToUse = this.preEnrollmentTempId ?? this.legalGuardianId;
    if (!idToUse) {
      return;
    }

    const request_url = this.updateUrlWithApiGatewayBaseUrl(INTEGRATION_URL);

    const payload: any = {
      image: data.selfie,
    };

    // Add the appropriate ID to payload
    if (this.preEnrollmentTempId) {
      payload.preEnrollmentTempId = this.preEnrollmentTempId;
    } else if (this.legalGuardianId) {
      payload.legalGuardianId = this.legalGuardianId;
    }

    const params = {
      endpointCode: detectFaceEndpoint,
      callerId: uuidv4(),
      payload,
      requester: uuidv4(),
    };

    return this.http.post<Record<string, unknown>>(request_url, params);
  }

  openVerificationModal(): void {
    // Check if we have either ID available
    const idToUse = this.preEnrollmentTempId ?? this.legalGuardianId;
    if (!idToUse) {
      return;
    }

    // Use a smaller modal size for better performance
    const modalRef = this.modalService.open(IremboSelfieVerificationComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      // Reduce overhead by minimizing animation
      animation: false,
    });

    Object.assign(modalRef.componentInstance, {
      phoneNumbers: this.phoneNumbers,
      preEnrollmentTempId: this.preEnrollmentTempId,
      legalGuardianId: this.legalGuardianId,
      maxAttempts: this.field.props?.['maxAttempts'] ?? 3,
      cameraNotAvailableErrorMessage:
        this.field.props?.['cameraNotAvailableMessage'],
      verificationErrorMessage:
        this.field.props?.['faceVerificationErrorMessage'],
      phoneNumberSelectionTitle:
        this.field.props?.['phoneNumberSelectionTitle'],
      citizenPhotoNotAvailableMessage:
        this.field.props?.['citizenPhotoNotAvailableMessage'],
      phoneNumberRequiredMessage:
        this.field.props?.['phoneNumberRequiredMessage'],
      verificationCodeSentSuccessMessage:
        this.field.props?.['verificationCodeSentSuccessMessage'],
      parentComponent: this,
      // Pass custom error message mappings
      customErrorMessages: this.field.props?.['customErrorMessages'] ?? {},
      // Pass the new face detection configuration
      useDetectFaceEndpoint:
        this.field.props?.['useDetectFaceEndpoint'] ?? false,
      detectFaceEndpoint: this.field.props?.['detectFaceEndpoint'],
      phoneConfig: {
        id: 'telephone',
        placeholder:
          this.field.props?.['phoneInputPlaceholder'] ?? 'Enter phone number',
        onlyCountries: this.field.props?.['phoneInputOnlyCountries'] ?? ['rw'],
        preferredCountries: this.field.props?.[
          'phoneInputPreferredCountries'
        ] ?? ['rw'],
        countryNoValidation:
          this.field.props?.['phoneInputCountryNoValidation'] ?? true,
        pattern: this.field.props?.['phoneInputPattern'] ?? '\\+2507\\d{8}',
      },
    });

    modalRef.result.then(
      result => {
        if (result?.selfie) {
          this.field.formControl?.setValue(
            this.translateService.instant(result.status)
          );

          if (result.status === 'verification_successful') {
            // Store the verified values for future comparison
            this.verifiedPhoneNumbers = JSON.stringify(this.phoneNumbers);
            this.verifiedPreEnrollmentId = this.preEnrollmentTempId;
            this.verifiedLegalGuardianId = this.legalGuardianId;

            this.verificationCompleted = true;
            this.cd.detectChanges();
          } else if (result.status === 'verification_code_requested') {
            setTimeout(() => {
              this.router.navigate(['/']);
            }, 100);
          }
        } else if (result && result.status === 'verification_code_requested') {
          setTimeout(() => {
            this.router.navigate(['/']);
          }, 100);
        }
      },
      () => {
        // Modal dismissed
      }
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
